(() => {
  const bannerMotionDuration = 400;

  const bindHeroCarousel = (hero) => {
    if (hero.dataset.heroCarouselBound) return;
    const track = hero.querySelector('[data-hero-carousel-track]');
    const slides = [...hero.querySelectorAll('[data-hero-slide]')];
    const dots = [...hero.querySelectorAll('[data-hero-dot]')];
    if (!track || !slides.length) return;

    hero.dataset.heroCarouselBound = 'true';
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncHeaderLogoTone = () => {
      document.documentElement.dataset.homeHeaderLogoTone = slides[activeIndex]?.dataset.headerLogoTone || 'blue';
    };
    const videos = slides.map((slide) => slide.querySelector('[data-hero-video]'));
    videos.forEach((video) => {
      if (!video) return;
      const markReady = () => video.classList.add('is-ready');
      if (video.readyState >= 2) markReady();
      else video.addEventListener('loadeddata', markReady, { once: true });
    });
    const syncVideoPlayback = () => {
      videos.forEach((video, index) => {
        if (!video) return;
        if (index !== activeIndex || reducedMotion.matches || document.hidden) {
          video.pause();
          return;
        }
        video.muted = true;
        video.play().catch(() => {});
      });
    };
    const syncActiveSlide = () => {
      syncHeaderLogoTone();
      syncVideoPlayback();
    };
    syncActiveSlide();
    if (slides.length < 2) {
      reducedMotion.addEventListener('change', syncVideoPlayback);
      document.addEventListener('visibilitychange', syncVideoPlayback);
      return;
    }

    const autoplaySeconds = Math.max(4, Number(hero.dataset.heroAutoplaySeconds) || 6);
    const autoplayEnabled = hero.dataset.heroAutoplay === 'true';
    let pointerStartX = null;
    let autoplayTimer = null;
    let paused = false;

    const render = (nextIndex) => {
      activeIndex = (nextIndex + slides.length) % slides.length;
      track.style.transform = `translate3d(${-activeIndex * 100}%, 0, 0)`;
      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.tabIndex = active ? 0 : -1;
      });
      dots.forEach((dot, index) => {
        const active = index === activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
      syncActiveSlide();
    };

    const stopAutoplay = () => {
      window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    };

    const scheduleAutoplay = () => {
      stopAutoplay();
      if (!autoplayEnabled || reducedMotion.matches || paused || document.hidden) return;
      autoplayTimer = window.setTimeout(() => {
        render(activeIndex + 1);
        scheduleAutoplay();
      }, autoplaySeconds * 1000);
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        render(Number(dot.dataset.heroTarget) || 0);
        scheduleAutoplay();
      });
    });

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      pointerStartX = event.clientX;
      stopAutoplay();
    });
    track.addEventListener('pointerup', (event) => {
      if (pointerStartX === null) return;
      const distance = event.clientX - pointerStartX;
      pointerStartX = null;
      if (Math.abs(distance) >= 36) render(activeIndex + (distance < 0 ? 1 : -1));
      scheduleAutoplay();
    });
    track.addEventListener('pointercancel', () => {
      pointerStartX = null;
      scheduleAutoplay();
    });

    hero.addEventListener('mouseenter', () => {
      paused = true;
      stopAutoplay();
    });
    hero.addEventListener('mouseleave', () => {
      paused = false;
      scheduleAutoplay();
    });
    hero.addEventListener('focusin', () => {
      paused = true;
      stopAutoplay();
    });
    hero.addEventListener('focusout', (event) => {
      if (hero.contains(event.relatedTarget)) return;
      paused = false;
      scheduleAutoplay();
    });
    const handleMotionPreferenceChange = () => {
      syncVideoPlayback();
      scheduleAutoplay();
    };
    const handleVisibilityChange = () => {
      syncVideoPlayback();
      scheduleAutoplay();
    };
    reducedMotion.addEventListener('change', handleMotionPreferenceChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    render(activeIndex);
    scheduleAutoplay();
  };

  const renderBanner = (slides, activeIndex) => {
    const banner = slides[0]?.closest('[data-home-banner]');
    if (!banner) return;
    const previousIndex = (activeIndex - 1 + slides.length) % slides.length;
    const nextIndex = (activeIndex + 1) % slides.length;

    slides.forEach((slide, index) => {
      slide.classList.remove(
        'le-home-module__banner-slide--previous',
        'le-home-module__banner-slide--active',
        'le-home-module__banner-slide--next',
        'is-hidden',
      );
      if (index === activeIndex) slide.classList.add('le-home-module__banner-slide--active');
      else if (index === previousIndex) slide.classList.add('le-home-module__banner-slide--previous');
      else if (index === nextIndex) slide.classList.add('le-home-module__banner-slide--next');
      else slide.classList.add('is-hidden');
      const active = index === activeIndex;
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.tabIndex = active ? 0 : -1;
    });

    banner?.querySelectorAll('[data-banner-dots] i').forEach((dot, index) => dot.classList.toggle('is-active', index === activeIndex));
    banner.dataset.homeBannerActiveIndex = String(activeIndex);
  };

  const rotateBanner = (slides, direction) => {
    const banner = slides[0]?.closest('[data-home-banner]');
    if (!banner || banner.dataset.homeBannerAnimating === 'true') return;
    banner.dataset.homeBannerAnimating = 'true';
    const currentIndex = Number(banner.dataset.homeBannerActiveIndex) || 0;
    renderBanner(slides, (currentIndex + direction + slides.length) % slides.length);
    window.setTimeout(() => {
      delete banner.dataset.homeBannerAnimating;
    }, bannerMotionDuration);
  };

  const bindBanner = (banner) => {
    if (banner.dataset.homeBannerBound) return;
    const slides = [...banner.querySelectorAll('[data-banner-slide]')];
    if (slides.length < 2) return;
    banner.dataset.homeBannerBound = 'true';
    const initialIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('le-home-module__banner-slide--active')));
    renderBanner(slides, initialIndex);
    banner.querySelector('[data-banner-prev]')?.addEventListener('click', () => rotateBanner(slides, -1));
    banner.querySelector('[data-banner-next]')?.addEventListener('click', () => rotateBanner(slides, 1));

    const track = banner.querySelector('.le-home-module__banner-track');
    let pointerStartX = null;
    track?.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      pointerStartX = event.clientX;
    });
    track?.addEventListener('pointerup', (event) => {
      if (pointerStartX === null) return;
      const distance = event.clientX - pointerStartX;
      pointerStartX = null;
      if (Math.abs(distance) < 36) return;
      rotateBanner(slides, distance < 0 ? 1 : -1);
    });
    track?.addEventListener('pointercancel', () => { pointerStartX = null; });
  };

  const bindCountdown = (node) => {
    if (node.dataset.homeCountdownBound) return;
    node.dataset.homeCountdownBound = 'true';
    const hoursNode = node.querySelector('[data-countdown-hours]');
    const minutesNode = node.querySelector('[data-countdown-minutes]');
    const secondsNode = node.querySelector('[data-countdown-seconds]');
    const update = () => {
      const offset = Number(node.dataset.utcOffsetMinutes || 0) * 60;
      const secondsToday = ((Math.floor(Date.now() / 1000) + offset) % 86400 + 86400) % 86400;
      const remaining = 86400 - secondsToday;
      const hours = Math.floor(remaining / 3600);
      const minutes = Math.floor((remaining % 3600) / 60);
      const seconds = remaining % 60;
      const hoursText = `${String(hours).padStart(2, '0')} ${node.dataset.hoursLabel || 'h'}`;
      const minutesText = `${String(minutes).padStart(2, '0')} ${node.dataset.minutesLabel || 'm'}`;
      const secondsText = `${String(seconds).padStart(2, '0')} ${node.dataset.secondsLabel || 's'}`;
      if (hoursNode && minutesNode && secondsNode) {
        hoursNode.textContent = hoursText;
        minutesNode.textContent = minutesText;
        secondsNode.textContent = secondsText;
      } else {
        node.textContent = `${hoursText} : ${minutesText} : ${secondsText}`;
      }
      node.setAttribute('aria-label', `${hoursText}, ${minutesText}, ${secondsText}`);
    };
    update();
    window.setInterval(update, 1000);
  };

  const bindShorts = (section) => {
    if (section.dataset.homeShortsBound) return;
    const rail = section.querySelector('[data-home-shorts-rail]');
    if (!rail) return;

    section.dataset.homeShortsBound = 'true';
    const rotate = (direction) => {
      const cards = [...rail.querySelectorAll('[data-home-short-card]')];
      if (cards.length < 2) return;
      if (direction > 0) rail.append(cards[0]);
      else rail.prepend(cards[cards.length - 1]);
    };

    section.querySelector('[data-home-shorts-prev]')?.addEventListener('click', () => rotate(-1));
    section.querySelector('[data-home-shorts-next]')?.addEventListener('click', () => rotate(1));

    const dialog = section.querySelector('[data-home-shorts-dialog]');
    const frame = dialog?.querySelector('[data-home-shorts-frame]');
    const status = dialog?.querySelector('[data-home-shorts-status]');
    const retry = dialog?.querySelector('[data-home-shorts-retry]');
    const destination = dialog?.querySelector('[data-home-shorts-destination]');
    if (!dialog || !frame || !status || !retry || !destination) return;

    let currentTrigger;
    let currentSource = '';
    let timeoutId;
    const loadingLabel = status.dataset.loadingLabel || status.textContent;
    const setState = (state) => {
      window.clearTimeout(timeoutId);
      dialog.dataset.contentState = state;
      status.hidden = state === 'ready';
      retry.hidden = state !== 'error';
      if (state === 'loading') {
        status.textContent = loadingLabel;
        timeoutId = window.setTimeout(() => setState('error'), 12000);
      }
      if (state === 'error') status.textContent = status.dataset.errorLabel || 'Video unavailable';
    };
    const loadCurrentSource = () => {
      if (!currentSource) return;
      setState('loading');
      frame.setAttribute('src', 'about:blank');
      window.requestAnimationFrame(() => frame.setAttribute('src', currentSource));
    };
    const closeDialog = () => {
      if (dialog.open) dialog.close();
    };

    frame.addEventListener('load', () => {
      if (frame.getAttribute('src') !== 'about:blank') setState('ready');
    });
    retry.addEventListener('click', loadCurrentSource);
    dialog.querySelector('[data-home-shorts-close]')?.addEventListener('click', closeDialog);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });
    dialog.addEventListener('close', () => {
      window.clearTimeout(timeoutId);
      frame.setAttribute('src', 'about:blank');
      currentTrigger?.focus();
    });

    section.querySelectorAll('[data-home-short-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        currentTrigger = trigger;
        currentSource = trigger.dataset.shortEmbedUrl || '';
        destination.setAttribute('href', trigger.dataset.shortDestinationUrl || '#');
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        loadCurrentSource();
      });
    });
  };

  const bindRecommendationsMosaic = (section) => {
    if (section.dataset.recommendationsMosaicBound) return;
    const groups = [...section.querySelectorAll('.le-recommendations-group--mosaic')];
    if (!groups.length) return;

    section.dataset.recommendationsMosaicBound = 'true';
    let frameId;
    const sync = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        const desktop = window.matchMedia('(min-width: 990px)').matches;
        groups.forEach((group) => {
          if (!desktop) {
            group.style.removeProperty('--le-rec-feature-media-runtime');
            return;
          }

          const featureMedia = group.querySelector('.product-card--feature-desktop .product-card__media');
          const compactMedia = [...group.querySelectorAll(
            '.product-card:not(.product-card--feature-desktop) .product-card__media',
          )];
          if (!featureMedia || compactMedia.length < 4) return;

          const firstTop = Math.min(...compactMedia.map((media) => media.getBoundingClientRect().top));
          const secondRow = compactMedia.filter((media) => media.getBoundingClientRect().top > firstTop + 1);
          if (!secondRow.length) return;

          const featureTop = featureMedia.getBoundingClientRect().top;
          const secondRowBottom = Math.max(...secondRow.map((media) => media.getBoundingClientRect().bottom));
          const nextHeight = Math.max(0, secondRowBottom - featureTop);
          const currentHeight = Number.parseFloat(
            group.style.getPropertyValue('--le-rec-feature-media-runtime'),
          ) || 0;
          if (Math.abs(currentHeight - nextHeight) > 0.5) {
            group.style.setProperty('--le-rec-feature-media-runtime', `${nextHeight}px`);
          }
        });
      });
    };

    sync();
    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(sync);
      observer.observe(section);
    } else {
      window.addEventListener('resize', sync, { passive: true });
    }
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-hero-carousel]').forEach(bindHeroCarousel);
    root.querySelectorAll('[data-home-banner]').forEach(bindBanner);
    root.querySelectorAll('[data-home-countdown]').forEach(bindCountdown);
    root.querySelectorAll('[data-home-shorts]').forEach(bindShorts);
    root.querySelectorAll('[data-home-section="recommendations"]').forEach(bindRecommendationsMosaic);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
